import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { Subscription } from 'rxjs';
import { IncidentTypesService, IncidentTypeViewModel } from 'src/app/boxwood-api';
import { IncidentEvent, IncidentType } from 'src/app/hickory-api';

export interface IncidentSearchTypeData {
  pageTitle?: string;
  selectMultiple?: boolean;
  selected?: IncidentType[];
}

@Component({
  selector: 'app-incident-search-type',
  templateUrl: './search-type.component.html',
  styleUrls: ['./search-type.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatProgressSpinnerModule,
    L10nTranslatePipe,
  ]
})
export class IncidentSearchTypeComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<IncidentSearchTypeComponent>);
  readonly data: IncidentSearchTypeData = inject(MAT_DIALOG_DATA) ?? {};
  readonly locale = inject(L10N_LOCALE);
  private readonly incidentTypesApi = inject(IncidentTypesService);

  readonly incidentTypes = signal<IncidentTypeViewModel[]>([]);
  readonly filteredTypes = signal<IncidentTypeViewModel[]>([]);
  readonly typesLoading = signal(true);
  filterText = '';

  private readonly disposables = new Subscription();

  ngOnInit() {
    this.fetchTypes();
  }

  ngOnDestroy() {
    this.disposables.unsubscribe();
  }

  fetchTypes() {
    this.disposables.add(
      this.incidentTypesApi.getIncidentTypesList().subscribe(result => {
        result.sort((a, b) => {
          const aName = a.name.values[this.locale.language] ?? a.name.values['en'];
          const bName = b.name.values[this.locale.language] ?? b.name.values['en'];
          return (aName > bName) ? 1 : -1;
        });
        this.incidentTypes.set(result);
        this.filteredTypes.set(result);
        this.typesLoading.set(false);
      })
    );
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  filterChanged() {
    // Filter the results based on the search text
    const filtered = this.incidentTypes().filter(type => {

      // Case insensitive search the type name for the current language
      const typeName = type.name?.values[this.locale.language] ?? type.name?.values['en'];
      const nameContains = typeName?.toLowerCase().includes(this.filterText.toLowerCase());

      if (nameContains) {
        return true;
      }

      // Case insensitive search the category name
      const categoryContains = type.category?.name?.toLowerCase().includes(this.filterText.toLowerCase());

      if (categoryContains) {
        return true;
      }

      // Case insensitive search all keywords for each type
      // Do this only if we don't match on other fields
      // This operation will take the longest
      return type.metadata.tags?.keywords?.some(el => {
        return el.toLowerCase().includes(this.filterText.toLowerCase());
      });
    });
    this.filteredTypes.set(filtered);
  }

  onTypeSelect(newType: IncidentTypeViewModel) {
    // Map between these types and add them to the list of selected types
    const incidentType: IncidentType = {
      id: newType.id,
      name: newType.name.values[this.locale.language] ?? newType.name.values['en'],
      showAssessment: newType.showAssessment,
      classification: newType.classification.name == "Emergency" ? "Emergency" : "Operations",
      loudToneSettings: newType.loudToneSettings,
    }

    const incidentEvent: IncidentEvent = {
      incidentCategory: newType.category,
      incidentType: incidentType,
    }

    const incidentEvents: IncidentEvent[] = [];
    incidentEvents.push(incidentEvent);

    const data: IncidentSearchTypeData = { selected: incidentEvents };

    this.dialogRef.close(data);
  }
}
